import {
  FetchNextPageOptions,
  InfiniteData,
  QueryKey,
  UseInfiniteQueryOptions,
  useInfiniteQuery,
} from '@tanstack/react-query'

import { peachApi } from 'core/api'
import * as R from 'core/helpers/remeda'
import { OperationData } from 'core/types/makeMutationHook'
import { PaginatedOperation, getQueryOptions } from 'core/types/makeQueryHook'
import {
  companyLoanConfigurationsGetAll,
  employeesGet,
  getEventsExternal,
  getMemberBulkOperationResultsByOperation,
  getProductsWithNotifications,
  getPromiseToPayPlansByLoan,
  globalNoteList,
  interactionGetAll,
  internalBorrowerSearch,
  internalEmployeesGet,
  internalListContactsForBorrower,
  listBorrowerNotes,
  listCaseNotes,
  listCasesByBorrower,
  loanTransactionsGetAll,
  loansGet,
  privateNoteList,
  supercaseBulkOperationList,
  teamsGet,
} from 'core/types/operations'

export const mergePagedQueries = <A, B, C>(
  queries: Array<{
    data: Array<A>
    pages: Array<B> | undefined
    isLoading: boolean
    fetchNextPage: (options?: FetchNextPageOptions) => Promise<C>
    hasNextPage: boolean
    isFetchingNextPage: boolean
  }>,
) => {
  const data = R.flatMap(queries, R.prop('data'))

  return {
    data,
    pages: R.flatMap(queries, R.prop('pages')),
    isLoading: queries[0].isLoading,
    hasNextPage: queries.some(R.prop('hasNextPage')),
    fetchNextPage: R.pipe(queries, R.find(R.prop('hasNextPage')))
      ?.fetchNextPage,
    isFetchingNextPage:
      queries[0].isFetchingNextPage ||
      R.drop(queries, 1).some(
        (query) => query.isLoading || query.isFetchingNextPage,
      ),
  }
}

const makePagedHook =
  <
    Resp extends PaginatedOperation,
    Key extends QueryKey,
    Vars extends { query?: unknown },
  >(
    endpoint: OperationData<Resp, Key, Vars>,
  ) =>
  (
    args: Vars & {
      options?: Partial<
        UseInfiniteQueryOptions<
          Resp,
          unknown,
          InfiniteData<Resp>,
          Resp,
          Key,
          string | null
        >
      >
    },
  ) => {
    const options = getQueryOptions(endpoint, args)

    const query = useInfiniteQuery({
      ...options,
      queryFn: (ctx) =>
        ctx.pageParam
          ? peachApi.get({ url: ctx.pageParam })
          : options.queryFn(),
      initialPageParam: null,
      getNextPageParam: (page) => page.nextUrl ?? null,
      getPreviousPageParam: (page) => page.previousUrl ?? null,
    })

    const data: NonNullable<Resp['data']> = R.flatMap(
      query.data?.pages ?? [],
      (page) => page.data ?? [],
    )

    const lastPage = R.last(query.data?.pages ?? [])

    return {
      ...query,
      data,
      pages: query.data?.pages,
      pagination: lastPage
        ? {
            ...R.pick(lastPage, ['total', 'nextUrl', 'previousUrl']),
            count: data.length,
          }
        : undefined,
    }
  }

export const useInternalBorrowerSearchPaged = makePagedHook(
  internalBorrowerSearch,
)

export const useBorrowerNotesPaged = makePagedHook(listBorrowerNotes)

export const useBulkOperationResultsPaged = makePagedHook(
  getMemberBulkOperationResultsByOperation,
)

export const useCaseNotesPaged = makePagedHook(listCaseNotes)

export const useCasesByBorrowerPaged = makePagedHook(listCasesByBorrower)

export const useEmployeesPaged = makePagedHook(employeesGet)

export const useInternalEmployeesGetPaged = makePagedHook(internalEmployeesGet)

export const useEventsPaged = makePagedHook(getEventsExternal)

export const useGlobalNotesPaged = makePagedHook(globalNoteList)

export const useInteractionPaged = makePagedHook(interactionGetAll)

export const useLoansPaged = makePagedHook(loansGet)

export const useLoanTransactionsPaged = makePagedHook(loanTransactionsGetAll)

export const usePrivateNotesPaged = makePagedHook(privateNoteList)

export const useProductsWithNotificationsPaged = makePagedHook(
  getProductsWithNotifications,
)

export const useSupercaseBulkOperationsPaged = makePagedHook(
  supercaseBulkOperationList,
)

export const useTeamsPaged = makePagedHook(teamsGet)

export const usePromiseToPayPlansByLoanPaged = makePagedHook(
  getPromiseToPayPlansByLoan,
)

export const useCompanyLoanConfigurationsGetAllPaged = makePagedHook(
  companyLoanConfigurationsGetAll,
)

export const useInternalListContactsForBorrowerPaged = makePagedHook(
  internalListContactsForBorrower,
)

export const useInteractionGetAllPaged = makePagedHook(interactionGetAll)
